import { PointArray } from '@editor/interfaces/attribute'
import {
  Box3,
  Box3Helper,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  ShapeUtils,
  Vector2,
  Vector3,
} from 'three'
import { Line2, LineGeometry, LineMaterial } from 'three-stdlib'

import { findPolygonCentroid } from './Polygon'

/**
 * Clear a helper plane mesh from the object.
 * Provide Scene if object was added directly to the scene.
 *
 * Removes all children from the object with the given name.
 *
 * @param object The Three.js Parent object to remove the helper from.
 * @param name The name of the helper mesh to remove.
 */
export const clearHelper = (object: Object3D, name: string) => {
  object.children
    .filter((child) => child.name === name)
    .forEach((child) => {
      object.remove(child)
    })
}

/**
 * Create a wireframe box using Line2 for thicker lines
 * @param corners Array of 8 Vector3 corners (3D OBB)
 * @param color Hex color of the line
 * @param linewidth Width of the line in world units
 * @returns Line2 object
 *
 * @example
 * useEffect(() => {
 *   if (workingPoints.length < 3) return
 *   const obb = new MinimumBoundingBox(workingPoints.map((pt) => new Vector3(pt[0], pt[1], pt[2])))
 *
 *   if (sceneRef.current) {
 *     addWireframeBox(sceneRef.current, 'min-bbox-wireframe', obb.getOBB3DCorners(), 0x00ff00)
 *   }
 * }, [sceneRef, workingPoints])
 */
export const addWireframeBox = (
  scene: Object3D,
  name: string,
  corners: Vector3[],
  color = 0xff0000,
  linewidth = 0.002,
) => {
  clearHelper(scene, name)

  // edges in pairs (12 edges)
  // prettier-ignore
  const edges: [number, number][] = [
    [0, 1], [1, 3], [3, 2], [2, 0], // bottom face
    [4, 5], [5, 7], [7, 6], [6, 4], // top face
    [0, 4], [1, 5], [2, 6], [3, 7], // vertical edges
  ];

  const positions: number[] = []
  edges.forEach(([i, j]) => {
    positions.push(corners[i].x, corners[i].y, corners[i].z, corners[j].x, corners[j].y, corners[j].z)
  })

  const geometry = new LineGeometry()
  geometry.setPositions(positions)

  const material = new LineMaterial({
    color,
    linewidth, // world units
    dashed: false,
    transparent: true,
  })

  const line = new Line2(geometry, material)
  line.name = name
  line.userData.type = 'debug'
  line.computeLineDistances()

  scene.add(line)
}

/**
 * Create a helper plane mesh in the scene.
 * Must provide at least 3 points.
 *
 * @param scene The Three.js scene to add the helper to.
 * @param name The name of the helper mesh.
 * @param points The 3D points defining the plane.
 *
 * @example
 * useEffect(() => {
 *   if (workingPoints.length < 3) return
 *   addPlaneHelper(sceneRef.current, 'polygon-plane-helper', workingPoints)
 *
 *   return () => {
 *     clearPlaneHelper(sceneRef.current, 'polygon-plane-helper')
 *   }
 * }, [sceneRef, workingPoints])
 */
export const addPlaneHelper = (scene: Object3D, name: string, points: PointArray[]) => {
  clearHelper(scene, name)

  const positionsV3 = points.map((p) => new Vector3(...p))
  const centroid = findPolygonCentroid(positionsV3)
  const matrix = new Matrix4().setPosition(centroid)
  const matrixInverse = matrix.clone().invert()
  const localPositions = positionsV3.map((pos) => pos.applyMatrix4(matrixInverse))

  const buffer = new BufferGeometry()
  const positions = new Float32Array(localPositions.length * 3)
  localPositions.forEach((vec, index) => {
    positions[index * 3] = vec.x
    positions[index * 3 + 1] = vec.y
    positions[index * 3 + 2] = vec.z
  })

  const positions2d = localPositions.map((pt) => new Vector2(pt.x, pt.y))
  const indi = ShapeUtils.triangulateShape(positions2d, [])

  buffer.setAttribute('position', new BufferAttribute(positions, 3))
  buffer.setIndex(indi.flat())
  buffer.computeVertexNormals()
  const helper = new Mesh(
    buffer,
    new MeshBasicMaterial({ color: 0xffff00, side: DoubleSide, opacity: 0.5, transparent: true }),
  )
  helper.name = name
  helper.userData.type = 'debug'
  helper.applyMatrix4(matrix)
  helper.scale.set(2, 2, 2)

  scene.add(helper)
}

/**
 * Add a Box3Helper to visualize a Box3 in the scene.
 *
 * @param scene Object3D to add the helper to
 * @param name Name of the helper object
 * @param box Box3 to visualize
 * @param color Color of the helper lines
 */
export const addBox3Helper = (scene: Object3D, name: string, box: Box3, color = 0x00ff00): Box3Helper => {
  clearHelper(scene, name)

  const boxHelper = new Box3Helper(box, color)
  boxHelper.name = name
  boxHelper.userData.type = 'debug'

  scene.add(boxHelper)
  return boxHelper
}
